import * as React from 'react';
import LocalDiv from '../localization/LocalDiv';
import { css } from '@emotion/core';
import SurveyHeader from './survey/SurveyHeader';
import { FLEX_ADAPTIVE } from '../layout/flexBox';
import ClamShell from '../layout/ClamShell';
import RaterStatusCard from './survey/raters/raterSummary/RaterStatusCard';
import Spacer from '../layout/Spacer';
import ProjectSteps from './ProjectSteps';
import ActionMsg from '../localization/ActionMsg';
import { Str } from '../localization/translationKeys';
import ProjectResources from './resources/ProjectResources';
import DevPlanSummary from './devPlan/DevPlanSummary';
import { useHistory } from 'react-router-dom';
import { useRoutes } from '../navigation/routes';
import ProjectLayout from './ProjectLayout';
import { onMobileDevice } from '../globalStyle/mediaQueries';
import { useMsgText } from '../localization/hooks';
import { useAppState } from '../core/redux/hooks';
import { useProjIds } from '../navigation/hooks';
import ErrorMsg from '../error/ErrorMsg';


const styles = {
  devPlan:         css`
    flex-grow: 1;
    flex-basis: 1px;
  `,
  customResources: css`
    flex-basis: 1px;
    flex-grow: 1;
  `,
  header:          css`
    margin-bottom: 40px;
    
    ${onMobileDevice} {
      margin-bottom: 35px;
    }
  `,
  actionBanner:    css`
    padding-bottom: 20px;
  `,
  projectSteps:    css`
    & > a { 
      margin-top: 80px; 
    }
  `,
};

type Props = {
  className?: string;
};


/**
 * ProjectSummary: a component that renders a basic overview of a project's status plus links out to the various bits
 *
 * PureComponent: true
 * Redux Connected: false
 */
const ProjectSummary: React.FC<Props> = props => {
  const history = useHistory();

  const projIds = useProjIds();

  const pageTitle = useMsgText(Str.Dashboard);

  const project   = useAppState(state => state.projectSummary);
  const resources = useAppState(state => state.projectResources);
  const projReqs  = useAppState(state => state.participantRequirements || []);

  const routes = useRoutes(projIds.participantId, projIds.projDisplayId);

  const showSurveyBanner = !project?.expired && !!project?.followupSurveyStep && !project?.followupSurveyCreated;
  const showInviteBanner = !showSurveyBanner
                           && !project?.expired
                           && project?.inviteRatersStep
                           && !project?.raterInvited
                           && !project.surveyPastDue;
  const showProjectSteps = project?.selfSurveyStep || project?.setGoalsStep || project?.viewReportStep;
  const showRaterStatus = project?.inviteRatersStep;

  const onCreateSurveyClick = React.useCallback(
    () => history.push(routes.CreateSurvey),
    [history, routes.CreateSurvey],
  );

  const onInviteRatersClick = React.useCallback(
    () => history.push(routes.InviteRaters),
    [history, routes.InviteRaters],
  );

  if (!project) {
    return <ErrorMsg error={Str.LoadFailed} />;
  }

  return (
    <ProjectLayout>
      <SurveyHeader
        title={pageTitle}
        deadline={new Date(project.surveyDeadline ?? new Date())}
        css={styles.header}
      />

      { // Invite Raters Banner
        <ClamShell show={showInviteBanner}>
          <div css={styles.actionBanner}>
            <ActionMsg
              msg={Str.DashInviteMsg}
              showBtn={true}
              btnMsg={Str.InviteRaters}
              onClick={onInviteRatersClick}
            />
          </div>
        </ClamShell>
      }
      { // Create Survey Banner
        <ClamShell show={showSurveyBanner}>
          <div css={styles.actionBanner}>
            <ActionMsg
              msg={Str.DashCreateSurveyMsg}
              showBtn={true}
              btnMsg={Str.CreateASurvey}
              onClick={onCreateSurveyClick}
            />
          </div>
        </ClamShell>
      }

      {
        (showRaterStatus) && (
          <RaterStatusCard
            routes={routes}
            proj={project}
            requirements={projReqs}
          />
        )
      }

      {
        (showProjectSteps) && (
          <ProjectSteps
            routes={routes}
            proj={project}
            css={css`& > a { margin-top: 80px; }`}
          />
        )
      }

      {/* Development Plan & Project Resources */}
      <LocalDiv css={[FLEX_ADAPTIVE, css`margin-top: 80px;`]}>
        {
          (project.developmentPlanStep) && (
            <>
              <DevPlanSummary routes={routes} plan={project} projectId={project.projectId} css={styles.devPlan} />
              <Spacer />
            </>
          )
        }
        <ProjectResources routes={routes} resources={resources} css={styles.customResources} />
      </LocalDiv>
    </ProjectLayout>
  );
};

export default ProjectSummary;
